import { ActionIcon, Badge } from '@mantine/core'
import { MAX_OBJECT_SUB_PARAMS, OBJECT_SUB_PARAM_NAME_PATTERN } from '@pubstack/common/src/bidder-seat'
import { ObjectBidParamKeyRename, ObjectSubParamSpec } from '@pubstack/common/src/bidder-seat'
import { OBJECT_SUB_PARAM_TYPES } from '@pubstack/common/src/bidderCatalog'
import { FunctionComponent } from 'react'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { BaseInputElement as BaseInput } from '~/components/Input'
import { UnstyledSelect } from '~/components/Select'

/** A row being edited. `type` is the select's value, so it stays a string until the form is read back. */
export type ObjectSubParamDraft = {
  name: string
  type: string
  /**
   * The name this row was loaded with, or `undefined` on a row added since. It is what tells a rename
   * from a delete plus an add, which look the same in the saved list, so the values already held under
   * the old name are moved instead of lost.
   */
  savedName?: string
  /** The type the key was saved with. Changing it clears the key's values, so the save must detect it. */
  savedType?: string
}

export type ObjectBidParamsDraft = Record<string, ObjectSubParamDraft[]>

const TYPE_OPTIONS = OBJECT_SUB_PARAM_TYPES.map((type) => ({ value: type, label: type, trackingLabel: `pbs-bidder-seat-object-param-type-${type}-set` as const }))

/**
 * Why a row cannot be saved, or `undefined`. Shown under the field, not on submit: the name is half of a
 * mapping column, and a save rejected after several rows were filled loses all of them.
 */
export const subParamNameError = (name: string, siblings: ObjectSubParamDraft[], index: number): string | undefined => {
  if (!name.trim()) return 'A key needs a name.'
  if (!OBJECT_SUB_PARAM_NAME_PATTERN.test(name)) return 'Letters, digits and underscores only — no dot.'
  return siblings.some((sibling, i) => i !== index && sibling.name === name) ? 'This key is already declared.' : undefined
}

export const hasObjectBidParamsError = (draft: ObjectBidParamsDraft): boolean => Object.values(draft).some((rows) => rows.some((row, index) => subParamNameError(row.name, rows, index) !== undefined))

/** The renames to send with the seat: a row that still has its saved name, but under a new one. */
export const toKeyRenames = (draft: ObjectBidParamsDraft): ObjectBidParamKeyRename[] =>
  Object.entries(draft).flatMap(([namespace, rows]) =>
    rows
      .filter((row): row is ObjectSubParamDraft & { savedName: string } => !!row.savedName && !!row.name.trim() && row.savedName !== row.name.trim())
      .map((row) => ({ namespace, from: row.savedName, to: row.name.trim() }))
  )

/**
 * The keys whose values a save clears because their type changed, not their name.
 *
 * What is filled in was typed under the old type, so it cannot be trusted under the new one: a number
 * column still holding `yes`.
 */
export const retypedKeys = (draft: ObjectBidParamsDraft): string[] =>
  Object.entries(draft).flatMap(([namespace, rows]) => rows.filter((row) => !!row.savedName && !!row.name.trim() && row.savedType !== row.type).map((row) => `${namespace}.${row.name.trim()}`))

/** The keys a save would delete, with the values they hold. This is what the confirmation must name. */
export const removedKeys = (saved: ObjectBidParamsDraft, draft: ObjectBidParamsDraft): string[] =>
  Object.entries(saved).flatMap(([namespace, rows]) => {
    const kept = new Set((draft[namespace] ?? []).map((row) => row.savedName).filter(Boolean))
    return rows.filter((row) => !kept.has(row.name)).map((row) => `${namespace}.${row.name}`)
  })

/** The shape sent to the server: drop empty rows, and the namespace itself once it holds none. */
export const toExtraObjectBidParams = (draft: ObjectBidParamsDraft): Record<string, ObjectSubParamSpec[]> | undefined => {
  const entries = Object.entries(draft)
    .map(([namespace, rows]) => [namespace, rows.filter((row) => row.name.trim()).map((row) => ({ name: row.name.trim(), type: row.type }) as ObjectSubParamSpec)] as const)
    .filter(([, rows]) => rows.length > 0)
  return entries.length ? Object.fromEntries(entries) : undefined
}

/**
 * Fixed tracks, laid out like the create-media rows. Both fields size themselves to the width of the
 * `input` inside them, around twenty characters, so in a flex row they fight for space and overflow the
 * delete button. The tracks decide the width instead, and `w-full min-w-0` on each field keeps it inside
 * its track. A key name is short, and a type is one of four short words.
 */
const ROW_GRID = 'grid grid-cols-[minmax(0,20rem)_140px_2rem] gap-3'

type Props = {
  /** The `object` params of the seat's catalog entry: the namespaces keys can be added to. */
  namespaces: string[]
  value: ObjectBidParamsDraft
  onChange: (value: ObjectBidParamsDraft) => void
  disabled?: boolean
}

/**
 * Declare the custom keys a bidder's `object` param carries, for example OneTag's `ext`.
 *
 * Each key becomes one mapping column named `ext.<key>`, so this screen decides what the spreadsheet
 * offers. No value is set here: values are filled in per ad unit, in the mapping.
 */
export const ObjectBidParamsSection: FunctionComponent<Props> = ({ namespaces, value, onChange, disabled }) => {
  const rowsOf = (namespace: string): ObjectSubParamDraft[] => value[namespace] ?? []

  const setRows = (namespace: string, rows: ObjectSubParamDraft[]) => onChange({ ...value, [namespace]: rows })

  const patchRow = (namespace: string, index: number, patch: Partial<ObjectSubParamDraft>) =>
    setRows(
      namespace,
      rowsOf(namespace).map((row, i) => (i === index ? { ...row, ...patch } : row))
    )

  return (
    <div className="flex flex-col gap-3">
      {namespaces.map((namespace) => {
        const rows = rowsOf(namespace)
        return (
          // No card: the title already sets the section apart, and a border would only take width from
          // the row.
          <div key={namespace} className="flex flex-col">
            <div className="mb-2 flex items-center gap-2">
              <span className="font-medium">
                Configuration of <code className="rounded bg-[var(--mantine-color-gray-1)] px-1.5 py-0.5 font-bold">{namespace}</code> parameter
              </span>
              <Badge variant="light" color={rows.length ? 'blue' : 'gray'} size="sm">
                {rows.length === 0 ? 'no key' : rows.length === 1 ? '1 key' : `${rows.length} keys`}
              </Badge>
            </div>

            {rows.length > 0 && (
              <div className={`${ROW_GRID} mb-1`}>
                <span className="text-xs font-medium text-[var(--mantine-color-gray-7)]">Key</span>
                <span className="text-xs font-medium text-[var(--mantine-color-gray-7)]">Type</span>
                <span />
              </div>
            )}

            <div className="flex flex-col gap-2">
              {rows.map((row, index) => {
                const error = subParamNameError(row.name, rows, index)
                return (
                  <div key={index} className={`${ROW_GRID} items-start`}>
                    <BaseInput
                      className="w-full min-w-0"
                      aria-label={`${namespace} key name`}
                      label="Key"
                      labelIsPlaceholder
                      value={row.name}
                      error={error}
                      disabled={disabled}
                      onChange={(event) => patchRow(namespace, index, { name: event.currentTarget.value })}
                    />
                    <UnstyledSelect
                      className="w-full min-w-0"
                      label="Type"
                      labelIsPlaceholder
                      options={TYPE_OPTIONS}
                      value={row.type}
                      disabled={disabled}
                      onChange={(option) => patchRow(namespace, index, { type: option.value as string })}
                    />
                    <div className="pt-2">
                      <ActionIcon
                        aria-label={`Remove ${namespace}.${row.name || 'key'}`}
                        variant="subtle"
                        color="gray"
                        disabled={disabled}
                        onClick={() =>
                          setRows(
                            namespace,
                            rows.filter((_, i) => i !== index)
                          )
                        }
                      >
                        <Icon name="delete" />
                      </ActionIcon>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* On the grid, so the button ends where the delete column ends, not at the panel edge. */}
            <div className={`${ROW_GRID} mt-4`}>
              <Button
                className="col-span-3 justify-self-end"
                variant="secondary"
                disabled={disabled || rows.length >= MAX_OBJECT_SUB_PARAMS}
                onClick={() => setRows(namespace, [...rows, { name: '', type: 'string' }])}
                trackingLabel="pbs-bidder-seat-object-param-new"
              >
                Add a key
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
